<template>
  <fieldset class="rounded-lg border border-gray-200 p-3 space-y-2">
    <legend class="px-1 text-sm font-semibold">Registration unit limits (optional)</legend>
    <p class="text-xs text-gray-600">
      {{ scope === 'Module'
        ? 'Module values override programme and student academic-calendar limits.'
        : 'Programme values override student academic-calendar limits. A module can override these values.' }}
      Leave either field blank to inherit that limit. Zero is an explicit limit.
      These limits apply per module for modular programmes, or per semester for regular programmes.
    </p>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label :for="`${idPrefix}-min`" class="block text-xs font-medium mb-1">Minimum units per registration</label>
        <InputNumber v-model="minimum" :inputId="`${idPrefix}-min`" :min="0" :maxFractionDigits="0"
          placeholder="Inherit minimum" class="w-full" inputClass="w-full"
          :invalid="v$.minimum.$invalid || !!minimumError" :aria-describedby="`${idPrefix}-min-error`" />
        <small v-if="minimumError || v$.minimum.$invalid" :id="`${idPrefix}-min-error`" class="text-red-500">
          {{ minimumError || 'Enter a whole number of zero or more.' }}
        </small>
      </div>
      <div>
        <label :for="`${idPrefix}-max`" class="block text-xs font-medium mb-1">Maximum units per registration</label>
        <InputNumber v-model="maximum" :inputId="`${idPrefix}-max`" :min="0" :maxFractionDigits="0"
          placeholder="Inherit maximum" class="w-full" inputClass="w-full"
          :invalid="v$.maximum.$invalid || !!maximumError" :aria-describedby="`${idPrefix}-max-error`" />
        <small v-if="maximumError || v$.maximum.$invalid" :id="`${idPrefix}-max-error`" class="text-red-500">
          {{ maximumError || localError }}
        </small>
      </div>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { integer, minValue } from '@vuelidate/validators';
import { registrationUnitLimitError } from '@/utils/registrationUnitLimits';

const minimum = defineModel<number | null>('minimum', { default: null });
const maximum = defineModel<number | null>('maximum', { default: null });
defineProps<{ scope: 'Module' | 'Programme'; idPrefix: string; minimumError?: string; maximumError?: string }>();
const localError = computed(() => registrationUnitLimitError({
  min_units_per_registration: minimum.value,
  max_units_per_registration: maximum.value,
}));
const v$ = useVuelidate({
  minimum: { integer, minValue: minValue(0) },
  maximum: { integer, minValue: minValue(0), validRange: () => !localError.value },
}, { minimum, maximum });
</script>
